import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import config from '@/config';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import fetchWrapper from '@/services/fetchWrapper.service';
import UserService from '@/services/user.service';
import StaticSeoHead from '@/shared-components/seo/StaticSeoHead';
import AppNavigationPage from '@/shared-components/wrappers/components/AppNavigationPage';
import { getSsrArtGamesBootstrap } from '@/utils/ssrArtGamesBootstrap';

import ArtGamesAmbient from './components/ArtGamesAmbient';
import ArtGamesHero from './components/ArtGamesHero';

import './index.css';

const games = [
  {
    id: 'kaleidoscope',
    titleKey: 'games.kaleidoscope.title',
    descriptionKey: 'games.kaleidoscope.description',
    statusKey: 'games.status.demo',
    ctaTo: '/newspaper/art-games/kaleidoscope',
    ctaLabelKey: 'games.kaleidoscope.cta',
  },
  {
    id: 'color-mix',
    titleKey: 'games.color_mix.title',
    descriptionKey: 'games.color_mix.description',
    statusKey: 'games.status.demo',
    ctaTo: '/newspaper/art-games/color-mix',
    ctaLabelKey: 'games.color_mix.cta',
  },
  {
    id: 'chromatic-music',
    titleKey: 'games.chromatic_music.title',
    descriptionKey: 'games.chromatic_music.description',
    statusKey: 'games.status.coming_soon',
    statusVariant: 'coming-soon',
    ctaLabelKey: 'games.chromatic_music.cta',
    notifyMessageKey: 'games.chromatic_music.notify_message',
    notifySuccessMessageKey: 'games.chromatic_music.notify_success',
  },
  {
    id: 'ippitsugaki',
    titleKey: 'games.ippitsugaki.title',
    descriptionKey: 'games.ippitsugaki.description',
    statusKey: 'games.status.demo',
    ctaTo: '/newspaper/art-games/ippitsugaki',
    ctaLabelKey: 'games.ippitsugaki.cta',
  },
];

function readInitialArtGamesSeoPayload() {
  const payload = getSsrArtGamesBootstrap('hub');
  return payload?.seoData && typeof payload.seoData === 'object' ? payload.seoData : null;
}

export default function NewspaperArtGames() {
  const t = useTranslations('common.newspaper.art_games');
  const { stateHandler } = useStateHandler();
  const [artGamesSeo, setArtGamesSeo] = useState(() => readInitialArtGamesSeoPayload());

  useEffect(() => {
    let ignore = false;

    async function loadArtGamesSeo() {
      try {
        const { response, data } = await fetchWrapper.get(`${config.apis.api.url}/seo/art-games/hub`);
        if (ignore || !response?.ok || !data || typeof data !== 'object') {
          return;
        }

        setArtGamesSeo(data);
      } catch (error) {
        if (!ignore) {
          console.error('Failed to load art games SEO payload', error);
        }
      }
    }

    loadArtGamesSeo();

    return () => {
      ignore = true;
    };
  }, []);

  const handleNotifyClick = async game => {
    const fallbackText = game?.notifyMessageKey
      ? t(game.notifyMessageKey)
      : t('games.default_notify_message');

    if (!game) {
      stateHandler.set('temporalPopup', { text: fallbackText });
      return;
    }

    if (game.id === 'chromatic-music') {
      try {
        const { response, data } = await UserService.notifyChromaticMusicLaunch();

        if (response?.ok) {
          const successText = game.notifySuccessMessageKey
            ? t(game.notifySuccessMessageKey)
            : data?.message || fallbackText;
          stateHandler.set('temporalPopup', { text: successText });
          return;
        }
      } catch (error) {
        console.warn('Chromatic Music notify failed', error);
      }
    }

    stateHandler.set('temporalPopup', { text: fallbackText });
  };

  return (
    <>
      <StaticSeoHead
        title={artGamesSeo?.title || t('meta.title')}
        description={artGamesSeo?.metaDescription || t('meta.description')}
        canonicalUrl={artGamesSeo?.canonicalUrl}
        canonicalPath={artGamesSeo?.canonicalPath || '/newspaper/art-games'}
        robots={artGamesSeo?.robots}
        ogType={artGamesSeo?.ogType}
        image={artGamesSeo?.ogImage}
        pageType={artGamesSeo?.pageType || 'CollectionPage'}
        structuredData={artGamesSeo?.structuredData}
      />
      <AppNavigationPage>
        <div className="art-games-page">
          <ArtGamesAmbient />

          <ArtGamesHero
            title={t('hero.title')}
            backLabel={t('common.back')}
            backRoute="/newspaper"
          />

          <section className="art-games-grid" aria-label={t('hero.grid_aria_label')}>
            {games.map(game => {
              const showStatus = Boolean(game.statusKey);
              const hasNotifyCta = Boolean(game.notifyMessageKey);

              return (
                <article key={game.id} className="art-game-card">
                  <div className="art-game-card__content">
                    {showStatus && (
                      <div className="art-game-card__top">
                        <span className={`art-game-card__status ${game.statusVariant ? `art-game-card__status--${game.statusVariant}` : ''}`}>
                          {t(game.statusKey)}
                        </span>
                        <span className="art-game-card__spark" aria-hidden="true">✶</span>
                      </div>
                    )}
                    <h2 className="art-game-card__title">{t(game.titleKey)}</h2>
                    <p className="art-game-card__description">{t(game.descriptionKey)}</p>
                  </div>
                  <div className="art-game-card__footer">
                    {hasNotifyCta ? (
                      <button
                        type="button"
                        className="art-game-card__cta art-game-card__cta--active"
                        onClick={() => handleNotifyClick(game)}
                      >
                        {game.ctaLabelKey ? t(game.ctaLabelKey) : t('games.default_notify_cta')}
                        <span aria-hidden="true" className="art-game-card__cta-icon">➜</span>
                      </button>
                    ) : game.ctaTo ? (
                      <Link to={game.ctaTo} className="art-game-card__cta art-game-card__cta--active">
                        {game.ctaLabelKey ? t(game.ctaLabelKey) : t('games.default_launch_cta')}
                        <span aria-hidden="true" className="art-game-card__cta-icon">➜</span>
                      </Link>
                    ) : (
                      <button className="art-game-card__cta" type="button" disabled>
                        {game.ctaLabelKey ? t(game.ctaLabelKey) : t('games.default_coming_cta')}
                        <span aria-hidden="true" className="art-game-card__cta-icon">➜</span>
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </section>

          <footer className="art-games-footer">
            <p className="art-games-footer__text">
              <span className="art-games-footer__lead">{t('footer.lead')}</span>{' '}
              {t('footer.body')}
            </p>
          </footer>
        </div>
      </AppNavigationPage>
    </>
  );
}
